import React from 'react';
import Stack from '@common/components/stack';
import Icon from '@common/components/icon';
import { useDrawerStore } from './drawer-store';
import Spinner from '@common/components/spinner';
import CompetencyContainerReadOnly from './competency-container-readonly';
import QuestionContainerReadOnly from './question-container-readonly';
import ContentModuleReadOnly from './content-module-readonly';
import { PerformanceQuestionType } from '@modules/performance/definitions';
import TalentReviewReadOnly from './talent-review-readonly';
import ProfileImage from '@common/components/profile-image';
import { t } from 'i18next';
import Card from '@common/components/card';
import { SimpleUser } from '@common/types/objects';
import { Button } from '@common/components/button';
import Placeholder from '@common/components/placeholder';

interface EvaluationDetailViewProps {
  onBack: () => void;
}

const EvaluationDetailView: React.FC<EvaluationDetailViewProps> = ({
  onBack,
}) => {
  const { drawerEvaluation, drawerLoading } = useDrawerStore();
  const [showRatingScales, setShowRatingScales] = React.useState(false);
  const [scalesToDisplay, setScalesToDisplay] = React.useState<any[]>([]);

  const evaluationData = drawerEvaluation?.data;
  const reviewee = evaluationData?.reviewee;
  const form = evaluationData?.form;

  const orderedComponents = React.useMemo(() => {
    const comps: any[] = form?.components || [];
    const sorted = [...comps].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0));
    return sorted;
  }, [form]);

  const componentsByType = React.useMemo(() => {
    const groups: Record<string, any[]> = {};
    const comps: any[] = form?.components || [];
    comps.forEach((c) => {
      const type = String(c?.type || 'unknown');
      if (!groups[type]) groups[type] = [];
      groups[type].push(c);
    });
    return groups;
  }, [form]);

  const responsesByComponentId = React.useMemo(() => {
    const answers: Record<string, { answersByUserId: Record<string, any> }> =
      {};
    const responseList: any[] = evaluationData?.responses || [];
    responseList.forEach((userResponse: any) => {
      const userId = String(userResponse?.user_id ?? '');
      const items: any[] = userResponse?.response || [];
      items.forEach((entry: any) => {
        const componentId = String(entry?.component_id ?? '');
        if (!componentId) return;

        if (
          entry?.type === 'competencies' &&
          typeof entry?.answer === 'object' &&
          'competency_id' in entry.answer
        ) {
          const competencyId = String(
            (entry.answer as { competency_id: string | number }).competency_id
          );
          if (!answers[competencyId]) {
            answers[competencyId] = { answersByUserId: {} };
          }
          answers[competencyId].answersByUserId[userId] = entry?.answer;
        }

        if (!answers[componentId]) {
          answers[componentId] = { answersByUserId: {} };
        }
        answers[componentId].answersByUserId[userId] = entry?.answer;
      });
    });
    return answers;
  }, [evaluationData?.responses]);

  const filteredCompetencyGroups = React.useMemo(() => {
    const revieweeCompetencies = evaluationData?.reviewee_competencies || [];
    if (!revieweeCompetencies.length) return [] as any[];

    const categoryMap: Record<
      string,
      { id: string; name: string; competencies: any[]; _idx: number }
    > = {};
    const encounteredCategoryIds: string[] = [];

    revieweeCompetencies.forEach((comp: any) => {
      const categoryId = String(comp?.category_id || comp?.category?.id || '');
      const categoryName = comp?.category?.name || t('performance:uncategorized');

      if (!categoryMap[categoryId]) {
        categoryMap[categoryId] = {
          id: categoryId,
          name: categoryName,
          competencies: [],
          _idx: encounteredCategoryIds.length,
        };
        encounteredCategoryIds.push(categoryId);
      }
      categoryMap[categoryId].competencies.push(comp);
    });

    return encounteredCategoryIds.map((cid) => {
      const { _idx, ...group } = categoryMap[cid] as any;
      return group;
    });
  }, [evaluationData?.reviewee_competencies]);

  const handleShowRatingScales = React.useCallback((scales: any[]) => {
    setScalesToDisplay(scales || []);
    setShowRatingScales(true);
  }, []);

  const handleHideRatingScales = React.useCallback(() => {
    setShowRatingScales(false);
    setScalesToDisplay([]);
  }, []);

  if (drawerLoading) {
    return (
      <Stack
        direction="column"
        gap={4}
        className="tw-p-4 tw-h-[calc(100vh-265px)]"
      >
        <Spinner centered />
      </Stack>
    );
  }

  if (!evaluationData) {
    return (
      <Stack direction="column" gap={4} className="tw-p-4">
        <Placeholder title={t('performance:no_evaluation_data_available')} />
      </Stack>
    );
  }

  return (
    <div className="tw-h-screen tw-flex tw-flex-col tw-bg-gray-50">
      <div className="tw-flex-shrink-0 tw-bg-white tw-z-10 tw-border-b tw-border-gray-300 tw-px-6 tw-py-5">
        {showRatingScales ? (
          <div className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-gap-3">
            <div className="tw-text-lg tw-font-semibold tw-text-gray-textDarkest tw-ml-[10px]">
              {t('performance:rating_scales')}
            </div>
            <Button
              type="transparent"
              icon="close"
              iconSize={24}
              size="small"
              className="!tw-p-0 !tw-text-gray-textGray"
              onClick={handleHideRatingScales}
            />
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
            <div
              className="tw-flex tw-flex-row tw-items-center tw-cursor-pointer"
              onClick={showRatingScales ? handleHideRatingScales : onBack}
            >
              <Icon type="chevron_left" size={24} color="grey" />
              <div className="tw-text-sm tw-font-medium tw-text-gray-muted">
                Go back
              </div>
            </div>
            <div className="tw-text-lg tw-font-semibold tw-text-gray-textDarkest tw-ml-[10px]">
              {form?.title}
            </div>
            <div
              className="tw-flex tw-flex-row tw-text-gray-textDarkest
                      tw-text-base tw-font-medium tw-items-center tw-gap-1 tw-ml-[10px]"
            >
              <ProfileImage
                size={20}
                user={reviewee as unknown as SimpleUser}
              />
              {reviewee?.full_name}
              <span className="tw-text-gray-text tw-text-sm tw-font-normal">
                ({t('performance:only_employee')})
              </span>
            </div>
          </div>
        )}
      </div>

      {showRatingScales ? (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-p-1">
          {scalesToDisplay?.map((scale) => (
            <Card key={scale.id} containerClassName="!tw-shadow-input">
              <Card.Content className="tw-flex tw-flex-col !tw-p-5 !tw-gap-5">
                <Stack direction="row" gap={2} align="start">
                  <div
                    className="tw-text-base tw-font-medium tw-text-white
                      tw-rounded-lg tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center"
                    style={{ backgroundColor: scale.color }}
                  >
                    {scale.index}
                  </div>
                  <Stack direction="column" gap={1}>
                    <div className="tw-text-gray-textDark tw-text-base tw-font-medium">
                      {scale.label}
                    </div>
                    {scale.description && (
                    <div className="tw-text-gray-text tw-whitespace-pre-line tw-text-sm-plus tw-font-normal">
                      {scale.description}
                    </div>
                      )}
                  </Stack>
                </Stack>
              </Card.Content>
            </Card>
            ))}
        </div>
        ) : (
          <div className="tw-flex-1 tw-overflow-y-auto tw-p-4 !tw-bg-background">
            <div className="tw-flex tw-flex-col tw-gap-6">
              {orderedComponents.map((comp: any) => {
                const type = String(comp?.type);
                if (type === PerformanceQuestionType.COMPETENCIES) {
                  return (
                    <CompetencyContainerReadOnly
                      key={String(comp?.id)}
                      evaluation={evaluationData}
                      isDrawerMode
                      drawerResponsesByComponentId={responsesByComponentId}
                      drawerFilteredCompetencyGroups={filteredCompetencyGroups}
                      onShowRatingScalesInDrawer={handleShowRatingScales}
                    />
                  );
                }
                if (type === PerformanceQuestionType.OPEN_QUESTION) {
                  return (
                    <QuestionContainerReadOnly
                      key={String(comp?.id)}
                      componentId={String(comp?.id)}
                      evaluation={drawerEvaluation}
                      componentsByType={componentsByType}
                      responsesByComponentId={responsesByComponentId}
                      isDrawerMode
                    />
                  );
                }
                if (type === PerformanceQuestionType.CONTENT_MODULE) {
                  return (
                    <ContentModuleReadOnly
                      key={String(comp?.id)}
                      component={comp}
                      isDrawerMode
                    />
                  );
                }
                if (type === PerformanceQuestionType.TALENT_REVIEW) {
                  return (
                    <TalentReviewReadOnly
                      evaluation={evaluationData}
                      responsesByComponentId={responsesByComponentId}
                      key={String(comp?.id)}
                      componentId={String(comp?.id)}
                      isDrawerMode
                    />
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}
    </div>
  );
};

export default EvaluationDetailView;
